import request from 'sb-request'
import actions from 'redux/actions'
import { camelCase } from 'lodash'
import { reducers, getState } from 'redux/core'
import { qRz, constants, getPrefixedAddressFields, getGqlQuery, snowplowContext } from 'common/helpers'
import { getCookieValue, getFeatureToggle } from 'helpers'


const _updateGender = (gender, withSelectedGender) => {
  if ([ 'female', 'male' ].indexOf(gender) === -1) {
    console.warn(`Unable to update user gender to: ${gender}`)
  }
  else {
    reducers.models.me.update({
      gender,
      selectedGender: withSelectedGender ? gender : undefined,
    })
  }
}

/**
 * Update gender in redux
 *
 * @param {String} gender
 */
const updateGender = (gender) => _updateGender(gender, false)

/**
 * Update gender in redux in case of the user's action (set selectedGender)
 *
 * @param {String} gender
 */
const updateSelectedGender = (gender) => _updateGender(gender, true)

const removeSelectedGender = () => reducers.models.me.removeSelectedGender()

/**
 * Sets new user profile and track login
 * @param profile
 */
const set = (profile) => {
  const { firstName, lastName, email, paymentOption } = profile

  qRz.push([
    'QrzTrackLoggedIn', {
      name: `${firstName} ${lastName}`,
      email,
    },
  ])

  reducers.models.me.update({ ...profile, isLoggedIn: true })

  // TODO revise when ssr is implemented
  actions.models.payment.update({ paymentOption })
}

/**
 * Fetches user profile
 *
 * @param {Promise} awaitFor
 */
const fetch = (awaitFor) =>
  request.get('rest/user/profile')
    .then((profile) => {
      if (awaitFor) {
        awaitFor.then(() => {
          set(profile)
        })
      }
      else {
        set(profile)
      }

      return profile
    })

const fetchStats = () =>
  request.get('rest/user/stats')
    .then((result) => reducers.models.me.update({ stats: result }))

/**
 * Fetch user stats (order status, products in queue, review  written, ratted products)
 * @param userId
 * @returns {*}
 */
const fetchUserStats = (userId) =>
  request.post('graphql?name=profileStat', {
    body: {
      query: `{
        stats: ${getGqlQuery('profileStat', { userId })} {
          orders
          queue
          reviews
          ratedProducts
        }
      }`,
    },
    modifyResult: ({ stats }) => ({
      // TODO OMG backend sends "null" sometimes....
      stats: stats || {
        orders: 0,
        queue: 0,
        reviews: 0,
        ratedProducts: 0,
      },
    }),
  })
    .then(({ stats }) => reducers.models.me.update({ stats }))

const fetchRedeemCoupon = () =>
  request.post('graphql?name=redeemCoupon', {
    body: {
      query: `{
        redeemCoupon {
          code
          coupon
          type
          shouldCharge
          chargeAmount
          months
        }
      }`,
    },
  })
    .then(({ redeemCoupon }) => {
      updateRedeemCoupon(redeemCoupon)
    })

const fetchBillingAndShippingInfo = () =>
  request.get('rest/user/address', {
    multiple: true,
  })
    .then((result) => {
      reducers.models.me.update(result)
      return result
    })

const fetchCardInfo = () =>
  request.get('rest/user/profile/card', {
    modifyResult: ({ account, lastDigits, accountHolderName, expirationDate }) => ({
      card: {
        cardNumber: account.slice(0, -4).replace(/./g, '*') + lastDigits,
        holderName: accountHolderName,
        expDate: expirationDate,
        lastDigits,
      },
    }),
  })
    .then((result) => {
      reducers.models.me.update(result)
      return result
    })

const update = (values) =>
  request.post('rest/user/profile', {
    body: values,
  })
    .then((result) => {
      reducers.models.me.update(result)
      return result
    })

const updateFinishedQuiz = (hasFinishedQuiz) =>
  reducers.models.me.update({ hasFinishedQuiz })

/**
 *
 * @param {String} type - "onboarding" || "recommendations" || "subscription"
 * @param {Boolean} value
 * @returns {*}
 */
const updateFinishedQuizNew = (type, value) =>
  reducers.models.me.updateFinishedQuiz({ type, value })

// Update shipping and billing

const updateShippingAndBillingInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  return request.post('rest/user/address', {
    body: {
      shippingFirstName:    data.shipping.firstName,
      shippingLastName:     data.shipping.lastName,
      shippingStreet:       data.shipping.street,
      shippingApt:          data.shipping.apt,
      shippingZipCode:      data.shipping.zipCode,
      shippingState:        data.shipping.state,
      shippingCity:         data.shipping.city,
      telephone:            data.shipping.telephone,
      billingFirstName:     data.billing.firstName,
      billingLastName:      data.billing.lastName,
      billingStreet:        data.billing.street,
      billingApt:           data.billing.apt,
      billingZipCode:       data.billing.zipCode,
      billingState:         data.billing.state,
      billingCity:          data.billing.city,
    },
    tokenName: 'addressUpdate',
  })
    .then(() => {
      reducers.models.me.update(data)

      if (isUndeliverable) {
        return actions.models.subscription.fetch()
      }
    })
    .then(() => data, () => Promise.reject())
}

const setAgeAndSkinType = ({ skinType = null, ageCategory = null }) => {
  reducers.models.me.setAgeAndSkinType({ ageCategory, skinType })
}

const updateShippingInfoInState = (data) =>
  reducers.models.me.update({ shipping: { ...data } })

const updateShippingInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  return request.post('rest/user/address/shipping', {
    body: {
      shippingFirstName:    data.firstName,
      shippingLastName:     data.lastName,
      shippingStreet:       data.street,
      shippingApt:          data.apt,
      shippingZipCode:      data.zipCode,
      shippingState:        data.state,
      shippingCity:         data.city,
      telephone:            data.telephone,
    },
    tokenName: 'addressUpdate',
  })
    .then(
      () => {
        if (isUndeliverable) {
          actions.models.subscription.fetch()
        }
        updateShippingInfoInState(data)
        return data
      },
      () => Promise.reject()
    )
}

const updateBillingInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  return request.post('rest/user/address/billing', {
    body: {
      billingFirstName:     data.firstName,
      billingLastName:      data.lastName,
      billingStreet:        data.street,
      billingApt:           data.apt,
      billingZipCode:       data.zipCode,
      billingState:         data.state,
      billingCity:          data.city,
      telephone:            data.telephone,
    },
    tokenName: 'addressUpdate',
  })
    .then(
      () => {
        if (isUndeliverable) {
          actions.models.subscription.fetch()
        }
        reducers.models.me.update({ billing: data })
        return data
      },
      () => Promise.reject()
    )
}

const changeGender = (gender) =>
  request.post('graphql', {
    withErrorModal: true,
    body: {
      query: `
        mutation {
          changeGender(
            gender: ${gender}
          )
        }
      `,
    },
  })
    .then(() => {
      _updateGender(gender, true)
    })


// Update card info

const beginUpdateCreditCardInfo = (data) => {
  const { routing: { location: { pathname } } } = getState()
  const { shipping = {}, billing = {}, isPaypal, plan, paymentMethod, promo, applePayValidationURL, placement } = data

  const isFreeCase = /case-card-update/.test(pathname)
  const formValues = getPrefixedAddressFields({ shipping, billing })

  const { page, pageType } = snowplowContext.get()
  const snowplowMetadata = { page, pageType }

  return request.post('rest/card-update/begin', {
    body: {
      ...formValues,
      isPaypal,
      plan,
      paymentType: paymentMethod, // TODO ask backend rename `paymentType` to `paymentMethod`
      freeCase: isFreeCase,
      promo,
      placement,
      applePayValidationURL,
      snowplowMetadata,
    },
    tokenName: 'cardUpdate',
  })
}

const commitUpdatePaymentError = (data) => {
  const { message, title, statusCode, resData } = data
  const { models: { subscription: { currPlan: { shippingPeriod, billingPeriod } } } } = getState()

  const isPrepaidCardModalVisible = (
    shippingPeriod === 1
    && billingPeriod < 12
    && resData && resData.authCode === '309'
    && !getCookieValue(constants.cookieNames.isPrepaidCardWithSubscriptionPlansModalHidden)
  )

  if (isPrepaidCardModalVisible) {
    actions.models.modals.open(constants.modals.prepaidCard.PrepaidCardWithSubscriptionPlans, {
      onChoose: (subscriptionPlanToUpgrade) => actions.models.payment.update({ subscriptionPlanToUpgrade }),
    })
  }
  else if (statusCode === 409) {
    if (message && title) {
      actions.models.modals.open(constants.modals.Dialog, {
        title,
        content: message,
        buttonTitle: 'Got it',
      })
    }

    actions.models.payment.update({ subscriptionPlanToUpgrade: null })
  }

  return Promise.reject()
}

const updateOrderInfo = (data) => {
  const { models: { subscription: isUndeliverable } } = getState()

  if (!data) {
    return
  }

  const { subscriptionOrderId, subscriptionPrice, subscriptionStatus, chargeReason } = data

  if (isUndeliverable) {
    actions.models.subscription.fetch()
  }
  else if (subscriptionStatus) {
    actions.models.subscription.updateStatus(subscriptionStatus)
  }

  if (subscriptionOrderId && subscriptionPrice) {
    reducers.models.payment.update({
      subscriptionOrderId,
      subscriptionPrice,
      subscriptionPlanToUpgrade: null,
      isUnpause: chargeReason === 'unpause',
    })
  }
}

const commitUpdatePaymentMethodInfo = (data) => {
  const { page, pageType } = snowplowContext.get()
  const snowplowMetadata = { page, pageType }

  return request.post('rest/card-update/commit', {
    body: {
      ...data,
      snowplowMetadata,
    },
    tokenName: 'cardUpdate',
    timeout: {
      response: 120 * 1000, // wait 120 sec
    },
    withErrorModal: false,
  })
    .then(updateOrderInfo, commitUpdatePaymentError)
    .then(fetch)
}

/**
 *
 * @param data
 * @param {Object} data.billing
 * @param {Object} data.creditCard
 */
// TODO remove promo and plan from commit when backend will be ready
const updateCreditCardInfo = (data) => {
  const { billing, shipping, promo, subscriptionPlanToUpgrade: plan, placement } = data

  return beginUpdateCreditCardInfo({
    placement,
    shipping,
    billing,
    promo,
    plan,
    paymentMethod: constants.paymentMethods.creditCard,
  })
    .then(
      ({ gatewaySessionId, vwsArgs, cardUpdateSession }) => (
        actions.models.payment.callVindicia({ ...data, gatewaySessionId, vwsArgs })
          .then(() => commitUpdatePaymentMethodInfo({ cardUpdateSession }))
          .then(() => reducers.models.me.update({ billing }))
          .then(() => reducers.models.payment.update({ subscriptionPlanToUpgrade: null }))
      ),
      () => Promise.reject()
    )
}

/**
 *
 * @param {object} data
 * @param {object} data.shipping
 * @param {object} data.billing
 * @param {string} data.promo
 */
const beginUpdatePayPalInfo = (data) => {
  const { shipping, billing, promo, placement } = data

  return beginUpdateCreditCardInfo({
    placement,
    shipping,
    billing,
    promo,
    paymentMethod: constants.paymentMethods.payPal,
    isPaypal: true,
  })
    .then(
      ({ paypalToken, cardUpdateSession }) => {
        actions.models.payment.update({ cardUpdateSession })

        return paypalToken
      },
      () => Promise.reject()
    )
}

const beginUpdateApplePayInfo = ({ applePayValidationURL, session, placement }) => {
  const { models: { me: { billing } } } = getState()

  return beginUpdateCreditCardInfo({
    billing,
    placement,
    paymentMethod: constants.paymentMethods.applePay,
    applePayValidationURL,
  })
    .then(
      ({ token, applePaySession, cardUpdateSession }) => {
        actions.models.requestTokens.update({ applePayPaymentToken: token })
        actions.models.payment.update({ cardUpdateSession })

        session.completeMerchantValidation(applePaySession)
      },
      ({ error: errorCode }) => {
        let error = new window.ApplePayError('unknown')

        if (errorCode === 'Unauthorized') {
          error = new window.ApplePayError('unknown', 'emailAddress')
        }

        session.completeMerchantValidation({
          errors: [ error ],
          newTotal: {
            label: 'Scentbird',
            amount: '0.00',
          },
        })

        return Promise.reject()
      }
    )
}

const commitUpdatePayPalInfo = (data) => {
  const { models: { payment: { cardUpdateSession } } } = getState()

  return commitUpdatePaymentMethodInfo({ ...data, cardUpdateSession })
}

const commitUpdateApplePayInfo = (session, { shipping, billing, applePayPaymentToken, token }) => {
  const {
    routing: { location: { pathname } },
    models: { payment: { cardUpdateSession } },
  } = getState()

  // TODO remove freeCase when backend will be ready
  const isFreeCase = /case-card-update/.test(pathname)
  const telephone = shipping.telephone || billing.telephone

  let formValues = getPrefixedAddressFields({ shipping, billing })

  const { page, pageType } = snowplowContext.get()
  const snowplowMetadata = { page, pageType }

  return request.post('rest/card-update/commit', {
    body: {
      ...formValues,
      telephone,
      applePayPaymentToken,
      cardUpdateSession,
      token,
      freeCase: isFreeCase,
    },
    tokenName: 'cardUpdate',
    timeout: {
      response: 120 * 1000, // wait 120 sec
    },
    withErrorModal: false,
    snowplowMetadata,
  })
    .then(
      (response) => {
        if (session.completePayment) {
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
        }

        updateOrderInfo(response)

        return Promise.all([
          updateShippingAndBillingInfo({ shipping, billing }),
          fetch(),
        ])
      },
      ({ err }) => {
        if (session.completePayment) {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE)
        }

        throw new Error(err)
      })
}

const sendBloggerInfo = (data) => {
  const {
    name, email, country, bloglink, readers, vloglink, subscribers, facebook, pinterest, twitter, instagram, comments,
  } = data

  const isBloggerRequestGQLEnabled = getFeatureToggle(constants.gqlFeatures.bloggerRequestGQL)

  if (isBloggerRequestGQLEnabled) {
    const bloggerRequestQuery = getGqlQuery('submitBloggerRequest', {
      input: {
        name,
        email,
        country,
        bloglink,
        readers,
        vloglink,
        subscribers,
        facebook,
        twitter,
        pinterest,
        instagram,
        comments,
      },
    })

    return request.post('graphql?name=bloggerRequest', {
      body: {
        query: `mutation {
          ${bloggerRequestQuery} {
            result
          }
        }`,
      },
    })
  }

  return request.post('/blogger_request', {
    body: data,
    formData: true,
  })
}

const removeStorageData = () => {
  actions.misc.cookies.removeItem(constants.cookieNames.offer)
  actions.misc.cookies.removeItem(constants.cookieNames.discountCoupon)
  // TODO remove cookies.clear
  // cookies.clear()
}

const getInvitedFriends = () =>
  request.get('rest/user/stats/invited')
    .then((data) => reducers.models.me.update({ invitedFriends: data }))

const inviteUser = (emails) =>
  request.post('rest/user/invite', {
    body: {
      emails,
    },
  })
    .then(() => getInvitedFriends())

const startInviteTracking = () => request.get('rest/user/tracking/start')

const stopInviteTracking = () => request.get('rest/user/tracking/end')

const getReferralCodes = () => {
  const { models: { me: { referralCodes } } } = getState()

  if (referralCodes) {
    return Promise.resolve()
  }

  return request.get('rest/user/referral-codes')
    .then((data) => reducers.models.me.update({ referralCodes: data }))
}

const getProductsStatus = () =>
  request.get('rest/user/stats/earned')
    .then((data) => reducers.models.me.update({ productsStatus: data }))

const validateAddress = (values) => {
  const { firstName, lastName, street, zipCode, city, state, apt } = values

  // TODO @grammka ask back to rework this fields
  const data = {
    firstname: firstName,
    lastname: lastName,
    street1: street,
    street2: apt,
    postcode: zipCode,
    city,
    region: state,
  }

  return request.post('rest/user/address/validate', {
    body: data,
    withErrorModal: false,
    modifyResult: ({ data = {}, isAlreadyValidated }) => {
      const newResult = {}

      Object.keys(data).forEach((key) => {
        const newKey = camelCase(key.replace(/shipping/g, ''))
        newResult[newKey] = data[key]
      })

      newResult.isAlreadyValidated = isAlreadyValidated

      return newResult
    },
  })
}

const updateRedeemCoupon = (redeemCoupon) => reducers.models.me.update({ redeemCoupon })

const updateRegisteredStatus = (isRegistered) => reducers.models.me.update({ isRegistered })

const fetchValidationOfSpecialCaseToken = (code) => {
  const params = {
    code,
  }

  return request.post('graphql?name=fetchValidationOfSpecialCaseToken', {
    withErrorModal: false,
    body: {
      query: `{
        ${getGqlQuery('validateSpecialCaseToken', params)} {
          errorCode
          valid
        }
      }`,
    },
  })
    .then(({ validateSpecialCaseToken: { valid } }) => {
      reducers.models.me.update({ isSpecialCaseTokenValid: valid })
    })
}


export default {
  updateGender,
  updateSelectedGender,
  removeSelectedGender,
  fetch,
  update,
  set,
  fetchStats,
  fetchUserStats,
  fetchCardInfo,
  updateFinishedQuiz,
  fetchBillingAndShippingInfo,
  getInvitedFriends,
  getReferralCodes,
  getProductsStatus,
  setAgeAndSkinType,
  updateShippingAndBillingInfo,
  updateShippingInfo,
  updateShippingInfoInState,
  updateBillingInfo,
  updateCreditCardInfo,
  beginUpdatePayPalInfo,
  commitUpdatePayPalInfo,
  commitUpdateApplePayInfo,
  beginUpdateApplePayInfo,
  sendBloggerInfo,
  removeStorageData,
  inviteUser,
  startInviteTracking,
  stopInviteTracking,
  validateAddress,
  updateRedeemCoupon,
  updateRegisteredStatus,
  fetchValidationOfSpecialCaseToken,
  fetchRedeemCoupon,
  changeGender,
  updateFinishedQuizNew,
}
